import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { styled } from "styled-components";
import Typo from "@/src/components/atom/Typo";
import { Button } from "@/src/components/atom/Button";
import PublicPageHeader from "@/src/components/organism/PublicPageHeader";
import PublicPageFooter from "@/src/components/molecule/PublicPageFooter";
import PaperAirplane from "@/src/assets/icons/icon-paper-airplane.svg";
import { useAppDispatch } from "@/src/store";
import { useSignOutMutation } from "@/src/store/apis/auth";
import { apiSlice } from "@/src/store/apis/baseQuery";
import { currencyApi } from "@/src/store/apis/currency";
import { resetTimer } from "@/src/store/slice/sessionTime";
import { persistor } from "@/src/index";
import PUBLIC_PATH from "@/src/routes/public/path";
import colorSet from "@/src/styles/color";

/**
 * AccountPendingApproval Component
 *
 * Displays a waiting page for users whose accounts are pending admin approval.
 * Matches Figma design: node-id=207-13660
 *
 * @component
 * @example
 * ```tsx
 * <Route path={PUBLIC_PATH.SIGNUP_PENDING_APPROVAL} element={<AccountPendingApproval />} />
 * ```
 *
 * Features:
 * - Displays pending approval status with paper airplane icon (80x80px)
 * - Shows informative message about the approval process
 * - Provides logout functionality with complete client-side state cleanup
 * - Redirects to login page after logout
 * - Matches exact Figma specifications for spacing, colors, and typography
 *
 * Design Specifications:
 * - Card width: 720px, padding: 48px horizontal / 40px vertical
 * - Gaps: 40px between main sections, 12px within icon section
 * - Button: aliceblue background (#F0F8FF), indigo text (#1C3D61)
 * - Shadow: 0px 0px 0px 1px rgba(0,0,0,0.1), 0px 4px 16px rgba(0,0,0,0.1)
 *
 * i18n Keys:
 * - signup:v2.approval.pageTitle - "대시보드 (제한된 접근)"
 * - signup:v2.approval.statusLabel - "가입 승인 요청"
 * - signup:v2.approval.title - "계정 승인 대기 중"
 * - signup:v2.approval.description1 - "해당 계정은 기업관리자의 승인을 기다리고 있습니다."
 * - signup:v2.approval.description2 - "승인이 완료되면 접근이 가능합니다."
 * - common:logout - "로그아웃"
 */
const AccountPendingApproval: React.FC = () => {
  const { t } = useTranslation(["signup", "common"]);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [signOut, { isLoading }] = useSignOutMutation();

  /**
   * Handles user logout with complete state cleanup
   * Follows the same pattern as MemberSignupResult for consistency
   *
   * Process:
   * 1. Call signOut API (continue even if it fails)
   * 2. Purge all persisted Redux state
   * 3. Reset all API caches (currency, base API)
   * 4. Reset session timer
   * 5. Navigate to login page with replace
   */
  const handleLogout = async () => {
    try {
      await signOut().unwrap();
    } catch (e) {
      // Continue with logout even if API call fails
      // User should still be able to log out from client side
    } finally {
      // Clean up all persisted state
      persistor.purge();
      dispatch(currencyApi.util.resetApiState());
      dispatch(apiSlice.util.resetApiState());
      dispatch(resetTimer());

      // Redirect to login page (replace history to prevent back navigation)
      navigate(PUBLIC_PATH.LOGIN, { replace: true });
    }
  };

  return (
    <Layout>
      <PublicPageHeader />
      <MainSection>
        <PageTitleContainer>
          <Typo typoType="d3" as="h1" color="gray1">
            {t("signup:v2.approval.pageTitle")}
          </Typo>
        </PageTitleContainer>

        <ApprovalCard>
          <IconSection>
            <Icon src={PaperAirplane} alt="Paper Airplane" />
            <Typo typoType="b9m" color="gray2">
              {t("signup:v2.approval.statusLabel")}
            </Typo>
          </IconSection>

          <ContentSection>
            <Typo typoType="h2" color="gray1">
              {t("signup:v2.approval.title")}
            </Typo>
            <DescriptionText>
              <Typo typoType="b7m" color="gray2">
                {t("signup:v2.approval.description1")}
              </Typo>
              <Typo typoType="b7m" color="gray2">
                {t("signup:v2.approval.description2")}
              </Typo>
            </DescriptionText>
          </ContentSection>

          <ButtonSection>
            <LogoutButton
              isLoading={isLoading}
              disabled={isLoading}
              onClick={handleLogout}
            >
              <Typo typoType="btn3m" color="indigo">
                {t("common:logout")}
              </Typo>
            </LogoutButton>
          </ButtonSection>
        </ApprovalCard>
      </MainSection>
      <PublicPageFooter />
    </Layout>
  );
};

export default AccountPendingApproval;

// ============================================================================
// Styled Components
// Matches Figma design specifications exactly
// ============================================================================

const Layout = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: ${colorSet.white};
`;

const MainSection = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 40px;
  gap: 40px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 40px 20px;
    gap: 32px;
  }
`;

const PageTitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 0;
  width: 100%;
  box-sizing: border-box;
`;

const ApprovalCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
  padding: 40px 48px;
  background-color: ${colorSet.white};
  border-radius: 16px;
  box-shadow:
    0px 0px 0px 1px rgba(0, 0, 0, 0.1),
    0px 4px 16px 0px rgba(0, 0, 0, 0.1);
  width: 720px;
  max-width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 32px 24px;
    gap: 32px;
    width: 100%;
  }
`;

const IconSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 200px;
`;

const Icon = styled.img`
  width: 80px;
  height: 80px;
  object-fit: contain;

  @media (max-width: 768px) {
    width: 64px;
    height: 64px;
  }
`;

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
  width: 100%;
`;

const DescriptionText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  text-align: center;
  width: 100%;
`;

const ButtonSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: center;
  width: 100%;
`;

const LogoutButton = styled(Button)`
  width: 100%;
  height: 40px;
  background-color: #f0f8ff;
  border: 0.5px solid rgba(23, 98, 195, 0.2);
  border-radius: 8px;
  box-shadow: 0px 1px 2px 0px rgba(5, 29, 57, 0.1);

  /* Accessible focus style (WCAG 2.1 AA compliant) */
  &:focus-visible {
    outline: 2px solid ${colorSet.indigo};
    outline-offset: 2px;
    box-shadow:
      0px 2px 4px 0px rgba(5, 29, 57, 0.2),
      0px 0px 0px 3px rgba(23, 98, 195, 0.15);
  }

  &:hover:not(:disabled) {
    box-shadow:
      0px 2px 4px 0px rgba(5, 29, 57, 0.2),
      0px 0px 0px 0.5px rgba(23, 98, 195, 0.2);
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;
